import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchMemberCategoriesByBusinessUnit, fetchMemberTypes, fetchBusinessUnitsByDomain } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function MemberCategoryListPage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [units, types] = await Promise.all([
        fetchBusinessUnitsByDomain(businessDomainId),
        fetchMemberTypes(businessDomainId),
      ]);
      const buIds = (Array.isArray(units) ? units : []).map((u) => u.businessUnitId);
      const catArrays = await Promise.all(
        buIds.map((buId) => fetchMemberCategoriesByBusinessUnit(buId).catch(() => []))
      );
      const cats = catArrays.flat();
      const typeMap = {};
      (Array.isArray(types) ? types : []).forEach((mt) => {
        typeMap[mt.memberTypeId] = mt.memberTypeName;
      });
      setRows(
        cats.map((c) => ({
          ...c,
          memberTypeName: typeMap[c.memberTypeId] || "",
        }))
      );
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  useEffect(() => { loadData(); }, [loadData]);

  const columns = [
    { field: "memberCategoryName", headerName: t("memberCategory.name"),       flex: 1, minWidth: 200 },
    { field: "memberTypeName",     headerName: t("memberCategory.memberType"), width: 200 },
  ];

  const handleRowClick = (params) => {
    navigate(`/strategy/${businessDomainId}/member-categories/${params.row.memberCategoryId}`);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("memberCategory.list.title")}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/member-categories/create`)}
        >
          {t("common.add")}
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.memberCategoryId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        onRowClick={handleRowClick}
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
        }}
      />
    </Box>
  );
}
